import React, { useState, useRef, useEffect, useContext } from "react";
import { Tooltip } from "@mui/material";
import { useOptions } from "../../OptionsContext";

function MaterialsDetailedTooltip({ materials, children, autoShow = false }) {
  const { options } = useOptions();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  // Refs вместо state: переcоздание materials не должно дёргать useEffect,
  // и таймеры нужно гасить надёжно (state замыкает старые значения).
  const hasAutoShownRef = useRef(false);
  const showTimerRef = useRef(null);
  const hideTimerRef = useRef(null);

  // Функция для расчета цен за м³, м² и за лист
  const calculateAdditionalPrices = (material) => {
    // Проверяем, относится ли материал к нужным категориям
    if (!material.category || !(
      material.category.includes("OSB") || 
      material.category.includes("Экструдированный пенополистирол") || 
      material.category.includes("Утеплитель")
    )) {
      return null;
    }

    // Получаем данные из options по названию материала
    const materialData = options[material.name] || {};
    
    // Расчет площади
    let area = 0;
    if (material.category.includes("OSB")) {
      area = parseFloat(String(materialData.AREA_OSB)?.replace(",", ".")) || 0;
    } else {
      area = parseFloat(String(materialData.AREA_SQUARE)?.replace(",", ".")) || 0;
    }

    // Расчет цены за м²
    const pricePerSquareMeter = area > 0 && material.pricePerOne > 0 
      ? Math.round(material.pricePerOne / area) 
      : 0;

    // Расчет цены за лист
    let pricePerSheet = 0;
    if (material.category.includes("OSB")) {
      // Для OSB цена уже указана за лист
      pricePerSheet = Math.round(material.pricePerOne);
    } else {
      const sheetsInsulation = parseInt(materialData.SHEETS_INSULATION) || 0;
      pricePerSheet = sheetsInsulation > 0 
        ? Math.round(material.pricePerOne / sheetsInsulation) 
        : 0;
    }

    // Расчет цены за м³ для утеплителей
    let pricePerCubicMeter = 0;
    if (material.category.includes("Утеплитель")) {
      // Используем готовое поле VOLUME_CUBIC - объем м³ в одной упаковке
      const volumeInCubicMeters = parseFloat(String(materialData.VOLUME_CUBIC)?.replace(",", ".")) || 0;
      
      if (volumeInCubicMeters > 0 && material.pricePerOne > 0) {
        // Цена за 1 м³ = цена за упаковку / объем м³ в упаковке
        pricePerCubicMeter = Math.round(material.pricePerOne / volumeInCubicMeters);
      }
    }

    return {
      area,
      pricePerSquareMeter,
      pricePerSheet,
      pricePerCubicMeter
    };
  };

  // Проверяем, есть ли материалы с нужными категориями
  const hasRelevantMaterials = materials.some(material => 
    material.category && (
      material.category.includes("OSB") || 
      material.category.includes("Экструдированный пенополистирол") || 
      material.category.includes("Утеплитель")
    )
  );

  // Автопоказ тултипа — РОВНО ОДИН РАЗ за всё время жизни компонента.
  // Раньше: `materials` в deps + state-таймер → каждый пересчёт materials
  // запускал новый setTimeout, старый hide-таймер не чистился → тултип
  // случайно «всплывал» сам по себе. Теперь — ref-флаг, ref-таймеры,
  // одна попытка автопоказа на монтирование.
  useEffect(() => {
    if (!autoShow || !hasRelevantMaterials || hasAutoShownRef.current) return;
    hasAutoShownRef.current = true;
    showTimerRef.current = setTimeout(() => {
      setTooltipOpen(true);
      hideTimerRef.current = setTimeout(() => setTooltipOpen(false), 10000);
    }, 2000);
    return () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [autoShow, hasRelevantMaterials]);

  if (!hasRelevantMaterials) {
    return children;
  }

  const tooltipContent = (
    <div>
      <div className="rd-materials-tooltip__title" style={{ fontWeight: 700, marginBottom: '8px', color: '#005ca9' }}>
        Детализация стоимости материалов:
      </div>
      {materials.filter(material => 
        material.category && (
          material.category.includes("OSB") || 
          material.category.includes("Экструдированный пенополистирол") || 
          material.category.includes("Утеплитель")
        )
      ).map((material, index) => {
        const additionalPrices = calculateAdditionalPrices(material);
        const materialData = options[material.name] || {};
        if (!additionalPrices) return null;
        
        // Определяем плотность для утеплителей
        const density = material.category.includes("Утеплитель") 
          ? (materialData.DENSITY || "—") 
          : null;
        
        return (
          <div key={index} style={{ marginBottom: '12px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>
              {material.name}
            </div>
            {/* За упаковку показываем только для утеплителей и экструдированного пенополистирола, но не для OSB */}
            {!material.category.includes("OSB") && (
              <div style={{ marginBottom: '4px' }}>
                <strong>За упаковку:</strong> {Math.ceil(material.pricePerOne).toLocaleString()} р.
              </div>
            )}
            {/* Цена за м³ только для утеплителей */}
            {material.category.includes("Утеплитель") && (
              <div style={{ marginBottom: '4px' }}>
                <strong>За м³:</strong> {additionalPrices.pricePerCubicMeter > 0
                  ? additionalPrices.pricePerCubicMeter.toLocaleString()
                  : "—"} р.
              </div>
            )}
            <div style={{ marginBottom: '4px' }}>
              <strong>За м²:</strong> {additionalPrices.pricePerSquareMeter > 0 
                ? additionalPrices.pricePerSquareMeter.toLocaleString() 
                : "—"} р.
            </div>
            <div style={{ marginBottom: density ? '4px' : '0' }}>
              <strong>За лист:</strong> {additionalPrices.pricePerSheet > 0 
                ? additionalPrices.pricePerSheet.toLocaleString() 
                : "—"} р.
            </div>
            {density && (
              <div style={{ marginBottom: '4px' }}>
                <strong>Плотность:</strong> {density} кг/м³
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const hoverProps = {
    onDetailedTooltipMouseEnter: () => setTooltipOpen(true),
    onDetailedTooltipMouseLeave: () => setTooltipOpen(false),
  };

  return (
    <Tooltip
      title={tooltipContent}
      placement="top"
      arrow
      open={tooltipOpen}
      onClose={() => setTooltipOpen(false)}
      disableHoverListener
      enterDelay={200}
      enterNextDelay={200}
      enterTouchDelay={0}
      PopperProps={{
        disablePortal: false,
        modifiers: [
          {
            name: 'flip',
            enabled: false,
          },
          {
            name: 'preventOverflow',
            enabled: false,
          },
        ],
      }}
      slotProps={{
        tooltip: {
          sx: {
            fontSize: '13.5px',
            lineHeight: 1.45,
            maxWidth: '320px',
            textAlign: 'left',
            padding: '12px 14px',
            backgroundColor: '#ffffff',
            color: '#2b3650',
            border: '1px solid #e3e9f2',
            borderRadius: '12px',
            boxShadow: '0 14px 32px -12px rgba(2, 2, 23, 0.30)',
            fontFamily: '"Inter", -apple-system, system-ui, "Segoe UI", Roboto, Arial, sans-serif',
            '& strong': { color: '#020217', fontWeight: 600 },
            '& .MuiTooltip-arrow': {
              color: '#ffffff',
              '&::before': {
                border: '1px solid #e3e9f2',
                boxSizing: 'border-box',
              },
            },
          },
        },
      }}
    >
      <div style={{ display: 'inline-block', verticalAlign: 'middle' }}>
        {React.cloneElement(children, hoverProps)}
      </div>
    </Tooltip>
  );
}

export default MaterialsDetailedTooltip;
